import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { v4 as uuid } from 'uuid'
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  Tooltip
} from '@chakra-ui/react'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import { capabilities, onlinePaymentWaivers } from '@homevest/utils'

import { ContentSectionCard } from 'components/TailwindUIToolkit'
import { getLeaseLedgerUrl, getLeasePaymentSettingsUrl } from 'constants/buildium'
import { hasCapability } from 'lib/admin-perms'
import { formatUTCDate } from 'lib/date-time'
import { onlinePaymentWaiversApi } from 'lib/online-payment-waivers-api'
import { StoreState } from 'store'

type OnlinePaymentStatus = onlinePaymentWaivers.OnlinePaymentStatus
type OnlinePaymentWaiverRequest = onlinePaymentWaivers.OnlinePaymentWaiverRequest
const { ONLINE_PAYMENT_WAIVER_NOTE_MAX_LENGTH } = onlinePaymentWaivers
const { LEDGER_VIEWER, ONLINE_PAYMENT_WAIVER_APPROVER } = capabilities.CAPABILITY_TYPES

const eligibilityMessages: Record<
  NonNullable<OnlinePaymentStatus['ineligibility_reason']>,
  string
> = {
  rental_not_found: 'This rental is unavailable.',
  rental_inactive: 'This rental is no longer active or pending.',
  missing_buildium_lease: 'This rental has no current Buildium lease.',
  active_eviction: 'A pending or filed eviction blocks a courtesy waiver.',
  not_failed_payment_restriction: 'There is no current failed-payment restriction to waive.'
}

const paymentLabel = (enabled: boolean | null) =>
  enabled === null ? 'Unknown' : enabled ? 'Enabled' : 'Disabled'

const PaymentStatusBadge = ({ enabled }: { enabled: boolean | null }) => (
  <Badge colorScheme={enabled === null ? 'gray' : enabled ? 'green' : 'red'} textTransform='none'>
    {paymentLabel(enabled)}
  </Badge>
)

const restrictionLabel = (reason: string | null) => {
  if (reason === 'nsf_threshold_exceeded' || reason === 'nsf_threshold_exceeded_observed') {
    return 'Failed payments'
  }
  return reason ? reason.replace(/_/g, ' ') : 'Restricted'
}
const terminalErrorMessages: Record<string, string> = {
  invalid_request:
    'The request was rejected. Review the reason and current payment status before trying again.',
  permission_denied: 'You do not have permission to approve this courtesy waiver.',
  rental_not_found: 'This rental is no longer available.',
  request_mismatch:
    'This request conflicts with a previously recorded request. No new waiver was issued. Contact operations.',
  stale_restriction:
    'The restriction changed. No new waiver was issued. Review the current status before considering another waiver.',
  ineligible_rental:
    'This rental is no longer eligible. No new waiver was issued. Review the current status.'
}

export default function Buildium({
  rentalId,
  buildiumLeaseId
}: {
  rentalId: string
  buildiumLeaseId?: number | null
}) {
  const admin = useSelector<StoreState, any>((state) => state.admin)
  const canViewStatus = hasCapability(admin, LEDGER_VIEWER)
  const canWaive = hasCapability(admin, ONLINE_PAYMENT_WAIVER_APPROVER)
  const [status, setStatus] = useState<OnlinePaymentStatus | null>(null)
  const [loadingStatus, setLoadingStatus] = useState(false)
  const [statusError, setStatusError] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [note, setNote] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [pendingRequest, setPendingRequest] = useState<OnlinePaymentWaiverRequest | null>(null)
  const [feedback, setFeedback] = useState<{
    message: string
    kind: 'success' | 'warning' | 'error'
  } | null>(null)
  const mounted = useRef(true)
  const submittingRef = useRef(false)

  async function refreshStatus() {
    setLoadingStatus(true)
    setStatusError(false)
    try {
      const current = await onlinePaymentWaiversApi.getStatus(rentalId)
      if (mounted.current) setStatus(current)
    } catch {
      if (mounted.current) setStatusError(true)
    } finally {
      if (mounted.current) setLoadingStatus(false)
    }
  }

  useEffect(() => {
    mounted.current = true
    if (canViewStatus && buildiumLeaseId) void refreshStatus()
    return () => {
      mounted.current = false
    }
    // The parent keys this card by rental, keeping requests tied to their original rental.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentalId, buildiumLeaseId, canViewStatus])

  async function submitWaiver() {
    if (submittingRef.current || !canWaive) return
    const trimmedNote = note.trim()
    if (
      !pendingRequest &&
      (!confirmed ||
        !trimmedNote ||
        trimmedNote.length > ONLINE_PAYMENT_WAIVER_NOTE_MAX_LENGTH ||
        !status?.eligible ||
        !status.restriction_id ||
        statusError)
    )
      return
    const request = pendingRequest || {
      request_id: uuid(),
      expected_restriction_id: status!.restriction_id!,
      note: trimmedNote
    }
    submittingRef.current = true
    setPendingRequest(request)
    setSubmitting(true)
    setFeedback(null)
    try {
      const result = await onlinePaymentWaiversApi.waive(rentalId, request)
      if (!mounted.current) return
      if (result.status) setStatus(result.status)
      if (result.outcome === 'applied' || result.outcome === 'already_applied') {
        setPendingRequest(null)
        setIsOpen(false)
        setFeedback({
          kind: 'success',
          message:
            result.outcome === 'applied'
              ? 'Online payments restriction courtesy waiver recorded!'
              : 'This courtesy waiver was already recorded. No additional waiver was issued.'
        })
      } else if (result.outcome === 'unresolved') {
        setFeedback({
          kind: 'warning',
          message:
            'The outcome is not yet confirmed. Retry this same request to check it safely; do not start a new waiver. Operations has been alerted.'
        })
      } else {
        setPendingRequest(null)
        setIsOpen(false)
        setFeedback({
          kind: result.outcome === 'superseded' ? 'warning' : 'error',
          message:
            result.outcome === 'superseded'
              ? 'The rental changed while this request was being resolved. The newer payment outcome was preserved. Review the current status before considering another waiver.'
              : 'The courtesy waiver was not recorded. Review the current status before trying again.'
        })
      }
    } catch (error) {
      if (!mounted.current) return
      const code =
        typeof error === 'object' && error !== null && 'code' in error
          ? String((error as { code: unknown }).code)
          : null
      if (code && terminalErrorMessages[code]) {
        setPendingRequest(null)
        setIsOpen(false)
        setFeedback({ kind: 'error', message: terminalErrorMessages[code] })
      } else {
        setFeedback({
          kind: 'warning',
          message:
            'The request could not be confirmed. Retry this same request to check its outcome safely. Your original reason and restriction will be reused.'
        })
      }
    } finally {
      submittingRef.current = false
      if (mounted.current) {
        setSubmitting(false)
        await refreshStatus()
      }
    }
  }

  function openModal() {
    if (!pendingRequest) {
      setNote('')
      setConfirmed(false)
      setFeedback(null)
    }
    setIsOpen(true)
  }

  return (
    <ContentSectionCard padding title='Buildium'>
      {buildiumLeaseId ? (
        <>
          <Text>
            <a href={getLeaseLedgerUrl(buildiumLeaseId)} target='_blank' rel='noopener noreferrer'>
              🔗 Financial Ledger
            </a>
            <br />
            <a
              href={getLeasePaymentSettingsUrl(buildiumLeaseId)}
              target='_blank'
              rel='noopener noreferrer'
            >
              🔗 Payment Settings
            </a>
          </Text>
          {canViewStatus && (
            <Box mt={4}>
              <HStack spacing={2} mb={3}>
                <Text fontWeight='semibold'>Online payments</Text>
                <Tooltip label='Refresh payment status'>
                  <IconButton
                    size='xs'
                    variant='outline'
                    aria-label='Refresh payment status'
                    icon={<ArrowPathIcon className='h-4 w-4' />}
                    onClick={() => void refreshStatus()}
                    isDisabled={loadingStatus || submitting}
                    _disabled={{
                      opacity: 1,
                      color: 'gray.500',
                      bg: 'gray.50',
                      cursor: 'not-allowed'
                    }}
                  />
                </Tooltip>
                {loadingStatus && (
                  <HStack role='status' spacing={2} color='gray.600'>
                    <Spinner size='sm' emptyColor='gray.200' aria-hidden='true' />
                    <Text fontSize='xs'>{status ? 'Refreshing…' : 'Loading…'}</Text>
                  </HStack>
                )}
              </HStack>
              {status && (
                <Box
                  fontSize='sm'
                  role='region'
                  aria-label='Online payment status'
                  aria-busy={loadingStatus}
                >
                  <Box
                    as='dl'
                    display='grid'
                    gridTemplateColumns='max-content minmax(0, 1fr)'
                    columnGap={4}
                    rowGap={2}
                    alignItems='baseline'
                    maxW='sm'
                  >
                    <Text as='dt' color='gray.600'>
                      Bank payments
                    </Text>
                    <Box as='dd' opacity={loadingStatus ? 0.55 : 1}>
                      <PaymentStatusBadge enabled={status.eft_enabled} />
                    </Box>
                    <Text as='dt' color='gray.600'>
                      Credit cards
                    </Text>
                    <Box as='dd' opacity={loadingStatus ? 0.55 : 1}>
                      <PaymentStatusBadge enabled={status.credit_card_enabled} />
                    </Box>
                    <Text as='dt' color='gray.600'>
                      Restriction
                    </Text>
                    <Box as='dd' opacity={loadingStatus ? 0.55 : 1}>
                      <Badge
                        colorScheme={
                          status.ineligibility_reason === 'active_eviction'
                            ? 'red'
                            : status.is_restricted
                            ? 'orange'
                            : 'gray'
                        }
                        textTransform='none'
                        whiteSpace='normal'
                      >
                        {status.ineligibility_reason === 'active_eviction'
                          ? 'Eviction'
                          : status.is_restricted
                          ? restrictionLabel(status.restriction_reason)
                          : 'None'}
                      </Badge>
                    </Box>
                    {status.is_restricted && (
                      <>
                        <Text as='dt' color='gray.600'>
                          Restriction started
                        </Text>
                        <Text as='dd' opacity={loadingStatus ? 0.55 : 1}>
                          {status.restriction_started_at
                            ? formatUTCDate(status.restriction_started_at, 'MMM d, yyyy')
                            : 'Not available'}
                        </Text>
                        <Text as='dt' color='gray.600'>
                          Expected end
                        </Text>
                        <Text as='dd' opacity={loadingStatus ? 0.55 : 1}>
                          {status.ineligibility_reason === 'active_eviction'
                            ? 'Not scheduled during eviction'
                            : status.restriction_expected_end_date
                            ? formatUTCDate(status.restriction_expected_end_date, 'MMM d, yyyy')
                            : 'Not available'}
                        </Text>
                      </>
                    )}
                  </Box>
                  {status.is_restricted && status.restriction_expected_end_date && (
                    <Text color='gray.600' fontSize='xs' mt={2}>
                      New late fees can extend this date.
                    </Text>
                  )}
                  {status.ineligibility_reason &&
                    status.ineligibility_reason !== 'not_failed_payment_restriction' && (
                      <Text color='gray.600' mt={2}>
                        {eligibilityMessages[status.ineligibility_reason]}
                      </Text>
                    )}
                </Box>
              )}
              {statusError && (
                <Alert status='error' mt={2}>
                  <AlertIcon />
                  Payment status could not be refreshed. Refresh it before starting a waiver.
                </Alert>
              )}
              {feedback && !isOpen && (
                <Alert status={feedback.kind} mt={3}>
                  <AlertIcon />
                  <Text>{feedback.message}</Text>
                </Alert>
              )}
              {canWaive && (pendingRequest || status?.eligible) && (
                <Button
                  display='block'
                  mt={3}
                  colorScheme='teal'
                  size='sm'
                  onClick={openModal}
                  isDisabled={submitting || (!pendingRequest && (loadingStatus || statusError))}
                >
                  {pendingRequest ? 'Check pending waiver' : 'Re-enable online payments'}
                </Button>
              )}
            </Box>
          )}
          <Modal
            isOpen={isOpen}
            onClose={() => {
              if (!submitting) setIsOpen(false)
            }}
            isCentered
            size='lg'
            closeOnOverlayClick={!submitting}
            closeOnEsc={!submitting}
          >
            <ModalOverlay />
            <ModalContent mx={4}>
              <ModalHeader>Re-enable online payments</ModalHeader>
              <ModalCloseButton isDisabled={submitting} />
              <ModalBody>
                <Text mb={3}>
                  This will let the tenant pay by ACH or credit card again, and reset their
                  failed-payment count to zero.
                </Text>
                <Text mb={4}>
                  After three more failed payments occur, online payments will be blocked again.
                </Text>
                {feedback && (
                  <Alert status={feedback.kind} mb={4}>
                    <AlertIcon />
                    <Text>{feedback.message}</Text>
                  </Alert>
                )}
                <FormControl isRequired>
                  <FormLabel htmlFor='online-payment-waiver-note'>
                    Reason for courtesy waiver
                  </FormLabel>
                  <Textarea
                    id='online-payment-waiver-note'
                    value={pendingRequest?.note ?? note}
                    onChange={(event) => setNote(event.target.value)}
                    maxLength={ONLINE_PAYMENT_WAIVER_NOTE_MAX_LENGTH}
                    isDisabled={submitting || !!pendingRequest}
                  />
                </FormControl>
                <Checkbox
                  mt={4}
                  isChecked={confirmed}
                  onChange={(event) => setConfirmed(event.target.checked)}
                  isDisabled={submitting || !!pendingRequest}
                >
                  I approve re-enabling payments and resetting the count.
                </Checkbox>
              </ModalBody>
              <ModalFooter>
                <Button
                  variant='ghost'
                  mr={3}
                  onClick={() => setIsOpen(false)}
                  isDisabled={submitting}
                >
                  {pendingRequest ? 'Close' : 'Cancel'}
                </Button>
                <Button
                  colorScheme='teal'
                  onClick={() => void submitWaiver()}
                  isLoading={submitting}
                  isDisabled={
                    !pendingRequest &&
                    (!confirmed ||
                      !note.trim() ||
                      note.trim().length > ONLINE_PAYMENT_WAIVER_NOTE_MAX_LENGTH ||
                      !status?.eligible ||
                      statusError ||
                      loadingStatus)
                  }
                >
                  {pendingRequest ? 'Retry same request' : 'Confirm and re-enable'}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </>
      ) : (
        <Text>No Buildium Lease!</Text>
      )}
    </ContentSectionCard>
  )
}
